import { AlertTriangle, Droplet, Wind, Sparkles, CheckCircle2 } from "lucide-react";

/**
 * Renders a simulated alerts feed for the host dashboard based on live property metrics.
 * Static generation is fine for MVP — real IoT signals will populate this feed via /api/iot/ingest.
 */
export default function HostAlertsFeed({ properties = [] }) {
  const alerts = buildAlerts(properties);
  if (alerts.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500 text-sm" data-testid="alerts-empty">
        No alerts. Your properties are running fresh.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="alerts-feed">
      {alerts.map((a) => (
        <div key={a.id} className={`flex items-start gap-4 p-5 border-b last:border-b-0 ${a.tone === "critical" ? "bg-red-50/40" : a.tone === "warn" ? "bg-amber-50/40" : ""}`}
          data-testid={`alert-${a.id}`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            a.tone === "critical" ? "bg-red-100 text-red-600" :
            a.tone === "warn"     ? "bg-amber-100 text-amber-700" :
                                    "bg-emerald-100 text-emerald-700"
          }`}>
            <a.icon className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold">{a.property}</div>
            <div className="mt-1 text-sm font-medium text-[#0A1128]">{a.title}</div>
            <div className="text-xs text-slate-500 mt-1">{a.detail}</div>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 whitespace-nowrap">{a.timeLabel}</div>
        </div>
      ))}
    </div>
  );
}

function buildAlerts(properties) {
  const out = [];
  properties.forEach((p, idx) => {
    const m = p.metrics || {};
    if (m.humidity !== undefined && m.humidity < 82) {
      out.push({
        id: `hum-${p.id}`, property: p.name, icon: Droplet,
        title: `Humidity dipped to ${m.humidity}%`,
        detail: "Comfort band is 82-92. Consider a small humidifier at check-in.",
        tone: m.humidity < 78 ? "critical" : "warn",
        timeLabel: `${(idx + 1) * 2}h ago`,
      });
    }
    if (m.air_quality !== undefined && m.air_quality < 88) {
      out.push({
        id: `aq-${p.id}`, property: p.name, icon: Wind,
        title: `Air quality drifted to ${m.air_quality}/100`,
        detail: "Open windows for 15 minutes after cleaning to reset the baseline.",
        tone: m.air_quality < 82 ? "critical" : "warn",
        timeLabel: `${(idx + 1)}h ago`,
      });
    }
    if (m.cleaning_verified !== undefined && m.cleaning_verified >= 96) {
      out.push({
        id: `clean-${p.id}`, property: p.name, icon: CheckCircle2,
        title: "Turnover cleaning verified",
        detail: `Cleaning quality: ${m.cleaning_verified}/100. Great work — badge stays live.`,
        tone: "good",
        timeLabel: `${(idx + 1) * 4}h ago`,
      });
    }
    if (p.freshness_score !== undefined && p.freshness_score >= 96) {
      out.push({
        id: `hi-${p.id}`, property: p.name, icon: Sparkles,
        title: `Freshness peaked at ${p.freshness_score}/100`,
        detail: "Marketing tip: share this milestone in your listing highlights.",
        tone: "good",
        timeLabel: `${(idx + 1) * 6}h ago`,
      });
    }
  });
  if (out.length === 0 && properties.length > 0) {
    out.push({
      id: "steady", property: properties[0].name, icon: AlertTriangle,
      title: "All signals steady",
      detail: "No anomalies detected in the last 24 hours.",
      tone: "good", timeLabel: "just now",
    });
  }
  return out.slice(0, 6);
}
